/**
 * Ce qu'il y a réellement dans l'air, et ce que ça veut dire.
 *
 * Les seuils retenus sont ceux qui comptent pour un choix immédiat, aérer ou
 * non, et non des moyennes annuelles réglementaires. Le monoxyde de carbone
 * et les particules fines sont les deux marqueurs de fumée d'incendie ;
 * l'ozone et le dioxyde d'azote complètent le tableau.
 *
 * Chaque polluant porte une explication en clair : un chiffre en µg/m³ ne
 * dit rien à personne, et le public de cette application n'est pas composé
 * de chimistes.
 */

export type PollutantKey = 'pm25' | 'pm10' | 'co' | 'o3' | 'no2' | 'so2';

export interface Pollutant {
  key: PollutantKey;
  name: string;
  short: string;
  unit: string;
  /** Bornes propre / correct / chargé. Au-delà de la dernière : très chargé. */
  thresholds: readonly [number, number, number];
  what: string;
}

export const POLLUTANTS: readonly Pollutant[] = [
  {
    key: 'pm25',
    name: 'Particules fines',
    short: 'PM2,5',
    unit: 'µg/m³',
    thresholds: [12, 25, 50],
    what: "les plus dangereuses : elles descendent jusqu'aux poumons",
  },
  {
    key: 'pm10',
    name: 'Poussières',
    short: 'PM10',
    unit: 'µg/m³',
    thresholds: [25, 50, 90],
    what: 'particules plus grosses, irritantes pour les voies respiratoires',
  },
  {
    key: 'co',
    name: 'Monoxyde de carbone',
    short: 'CO',
    unit: 'µg/m³',
    thresholds: [1000, 4000, 10000],
    what: "gaz des fumées d'incendie : il prend la place de l'oxygène dans le sang",
  },
  {
    key: 'o3',
    name: 'Ozone',
    short: 'O₃',
    unit: 'µg/m³',
    thresholds: [60, 120, 180],
    what: 'se forme au soleil, irrite la gorge et les yeux',
  },
  {
    key: 'no2',
    name: "Dioxyde d'azote",
    short: 'NO₂',
    unit: 'µg/m³',
    thresholds: [25, 50, 100],
    what: 'vient surtout du trafic routier',
  },
  {
    key: 'so2',
    name: 'Dioxyde de soufre',
    short: 'SO₂',
    unit: 'µg/m³',
    thresholds: [20, 50, 150],
    what: "vient surtout de l'industrie",
  },
];

export type LevelIndex = 0 | 1 | 2 | 3;

export const LEVEL_WORDS: readonly [string, string, string, string] = [
  'propre',
  'correct',
  'chargé',
  'très chargé',
];

/** Noms de variables CSS : aucune couleur en dur dans le cœur. */
export const LEVEL_COLOR_VARS: readonly [string, string, string, string] = [
  '--ok',
  '--ok',
  '--mid',
  '--bad',
];

export function levelIndex(
  value: number,
  thresholds: readonly [number, number, number],
): LevelIndex {
  if (value < thresholds[0]) return 0;
  if (value < thresholds[1]) return 1;
  if (value < thresholds[2]) return 2;
  return 3;
}

export function pollutantByKey(key: PollutantKey): Pollutant {
  const found = POLLUTANTS.find((pollutant) => pollutant.key === key);
  // POLLUTANTS couvre exhaustivement PollutantKey : ce cas est inatteignable.
  if (!found) throw new Error(`Polluant inconnu : ${key}`);
  return found;
}
